import { Icon } from "semantic-ui-react";

/* Semantic UI's own `circular` icon sizing is em-based and renders far
   bigger than its size keywords suggest in this app's font context —
   building the circles as plain divs gives predictable, exact pixel
   control instead of fighting that. */
const COLOR_HEX = {
  green: "#21ba45",
  orange: "#f2711c",
  blue: "#2185d0",
  yellow: "#fbbd08",
  red: "#db2828",
  grey: "#767676",
};

const BASE_SIZE = 52;
const BADGE_SIZE = 24;
const RADIUS = BASE_SIZE / 2;
const BADGE_ORBIT = RADIUS + 4; // badge centers sit a bit past the base circle's edge, not flush on it
const ANGLE_STEP = 46; // degrees between badges along the arc
const START_ANGLE = -60; // 1 o'clock, measured from 3 o'clock = 0°
const RING = "0 0 0 2px white";

function Circle({ size, bg, iconName, text, zIndex, style }) {
  return (
    <div
      style={{
        position: "absolute",
        width: size,
        height: size,
        borderRadius: "50%",
        background: bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: RING,
        zIndex,
        ...style,
      }}
    >
      {text ? (
        /* Count badges (e.g. a triple room's "×3") read better as compact
           text than as three separate bed glyphs crowding the circumference. */
        <span style={{ color: "white", fontWeight: 700, lineHeight: 1, fontSize: size * 0.42 }}>
          {text}
        </span>
      ) : (
        /* Semantic's icon font glyphs aren't vertically centered within their
           own line box — lineHeight:1 collapses that box down to the glyph's
           actual ink so flex centering lands it in the middle of the circle
           instead of low with empty space above it. */
        <Icon name={iconName} inverted style={{ margin: 0, lineHeight: 1, fontSize: size * 0.5 }} />
      )}
    </div>
  );
}

/* The first badge always sits at 1 o'clock, with each following badge
   continuing clockwise along the same circumference (2, 3, 4 o'clock…). */
function badgeAngles(count) {
  return Array.from({ length: count }, (_, i) => START_ANGLE + i * ANGLE_STEP);
}

/**
 * Composes one large, centered base icon with small badge icons arranged
 * along its circumference — each badge's own center sits just past the
 * base circle's edge, curving around it rather than stacking in a column.
 * Generic — not tied to rooms, reusable anywhere a "main icon + small
 * annotations" glyph is needed.
 *
 * @param {string} baseIcon - Semantic UI icon name for the main icon.
 * @param {string} color - Semantic UI color name applied to every icon in the composition.
 * @param {(string|{text:string}|{icon:string,color:string})[]} badges - Semantic UI
 *   icon names (inherit the base icon's color), `{ text }` for a short text badge
 *   (e.g. "×3", also inherits the base color), or `{ icon, color }` for a badge
 *   with its own color independent of the base icon (e.g. an orange overdue
 *   warning on a green "Occupied" room) — placed around the base icon's edge
 *   in order.
 */
export default function CombinedIcon({ baseIcon, color, badges = [] }) {
  const bg = COLOR_HEX[color] || COLOR_HEX.grey;
  const angles = badgeAngles(badges.length);
  /* The box is sized to the base icon only, so it — and therefore the base
     icon itself — stays perfectly centered regardless of badge count.
     Badges are positioned relative to that same center and are free to
     visually overflow the box's own bounds (no overflow:hidden here) since
     they only ever extend to the right, where the room tile has spare
     margin for them. */
  const center = RADIUS;

  return (
    <div style={{ position: "relative", width: BASE_SIZE, height: BASE_SIZE, margin: "0 auto" }}>
      <Circle size={BASE_SIZE} bg={bg} iconName={baseIcon} zIndex={1} style={{ left: 0, top: 0 }} />
      {badges.map((badge, i) => {
        const rad = (angles[i] * Math.PI) / 180;
        const bx = center + BADGE_ORBIT * Math.cos(rad) - BADGE_SIZE / 2;
        const by = center + BADGE_ORBIT * Math.sin(rad) - BADGE_SIZE / 2;
        const isText = badge && typeof badge === "object" && "text" in badge;
        const isOwnColor = badge && typeof badge === "object" && "icon" in badge;
        return (
          <Circle
            key={i}
            size={BADGE_SIZE}
            bg={isOwnColor ? COLOR_HEX[badge.color] || bg : bg}
            iconName={isText ? null : isOwnColor ? badge.icon : badge}
            text={isText ? badge.text : null}
            zIndex={10 + i}
            style={{ left: bx, top: by }}
          />
        );
      })}
    </div>
  );
}
